import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import { Contact, EmailInformation, User, useUserByContactIdLazyQuery } from "@repo/server-api";
import { useTranslation } from "next-i18next";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";

import { validErrorState } from "@repo/legacy/components/helper/Index";

import UserSection from "./UserSection";
import DialogLoginUser from "../dialogs/DialogLoginUser";

const LoginSection = forwardRef(
	(
		props: Readonly<{
			user: User | null;
			setUser: (value: User) => void;
			contact: Contact | null;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const [isLoaded, setIsLoaded] = useState<boolean>(false);
		const [user, setUser] = useState<User>({
			contactId: null,
			email: "",
			emailVerified: false,
			enabled: false,
			groups: [],
			id: "",
			username: "",
			firstName: "",
			lastName: "",
		});
		const [hasEmail, setHasEmail] = useState<boolean>(false);
		const [openDialog, setOpenDialog] = useState<boolean>(false);

		const [errors, setErrors] = useState<{
			user?: Record<string, boolean | object | string>;
		}>({});

		// const allUsersDataSource = useAllUsersDataSource();

		const [getUserByContactId] = useUserByContactIdLazyQuery({
			variables: { id: props.contact?.id ?? "" },
			fetchPolicy: "no-cache",
			onCompleted: (data) => {
				if (data.userByContactId) {
					setUser(data.userByContactId);
					setIsLoaded(true);
				}
			},
		});

		useEffect(() => {
			if (props.contact) {
				getUserByContactId();
				setHasEmail(
					!!props.contact.additionalInformation?.find(
						(info) => (info as EmailInformation).__typename === "EmailInformation",
					),
				);
			}
		}, [props.contact, getUserByContactId]);

		useEffect(() => {
			if (props.user && !isLoaded) {
				setUser({
					...props.user,
				});
				setIsLoaded(true);
			}
		}, [props.user, isLoaded]);

		useEffect(() => {
			if (isLoaded) props.setUser(user);
		}, [user, isLoaded, props]);

		const userRef = React.useRef<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		} | null>(null);

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};

			localErrors.user = (await userRef.current?.validate()) || {};
			setErrors(localErrors);

			return {
				user: validErrorState(localErrors) ? "" : t("login.error", { ns: "validation" }),
			};
		}, [t]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		return (
			<>
				{user.id ? (
					<UserSection
						isDialog={false}
						ref={userRef}
						user={user}
						setUser={setUser}
						contact={props.contact}
						errors={errors.user}
						setErrors={(value) => setErrors({ ...errors, user: { ...errors.user, ...value } })}
					/>
				) : (
					<>
						<Button
							className="greenButton"
							variant="contained"
							onClick={() => {
								setUser({
									contactId: null,
									email: "",
									emailVerified: false,
									enabled: false,
									groups: [],
									id: "",
									username: "",
									firstName: "",
									lastName: "",
								});
								setOpenDialog(true);
							}}
							disabled={!hasEmail}>
							{t("create-login")} <AddIcon />
						</Button>
						{!hasEmail && <p className="red">{t("no-email-warning")}</p>}
						<DialogLoginUser
							open={openDialog}
							setOpen={setOpenDialog}
							user={user}
							setUser={setUser}
							refreshData={() => getUserByContactId()}
							contact={props.contact}
						/>
					</>
				)}
			</>
		);
	},
);

LoginSection.displayName = "LoginSection";
export default LoginSection;
